import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { getSessionFromCookie } from "../components/utils/cognito";
import { getEanByInstallationId } from "../api/documents";
import { endOfDay, format, startOfDay, subDays } from "date-fns";

// ** Import our custom DateRangePicker **
import { useMediaQuery } from "react-responsive";

// Child chart components
import DayAheadMarketPriceChart from "../components/charts/DayAheadMarketPriceChart";
import SupplyChart from "../components/charts/SupplyChart";
import TotalAmountChart from "../components/charts/TotalAmountChart";
import PeakPowerChart from "../components/charts/PeakPowerChart";
import CustomDateRangePicker from "../components/utils/CustomDateRangePicker";
import InfoIcon from "../components/svg/InfoIcon";
import Loading from "../components/ui/Loading";
import { exportToCSV, exportToPDF } from "../components/utils/exportUtils";
import DownloadIcon from "../components/svg/DonwloadIcon";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../store/indexRedux";

// New interface for the API response

interface MeterValueItem {
  dtutc: string;
  dtlt: string;
  date: string;
  hour: number;
  qhour: number;
  ean: string;
  mv: string;
  pl: string;
  price: string;
  direction: string;
  belpex: string;
  max_mv?: string | number; // Add max_mv field for hourly/monthly data
  max_mv_dtlt?: string; // Optional timestamp of max power
}

const ServiceDashboardPage: React.FC = () => {
  const { serviceId, type } = useParams<{ serviceId: string; type: "injection" | "offtake" }>();
  const navigate = useNavigate();
  const { t } = useTranslation("serviceDashboard");

  // Redux and session info
  const selectedUserCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);
  const companyId = selectedUserCompany?.company_id;
  const session = getSessionFromCookie();
  const token = session?.getAccessToken().getJwtToken();

  // Local state - updated to use new data structure
  const [ean, setEan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MeterValueItem[] | null>(null);
  const [belpexArray, setBelpexArray] = useState<Array<{ dtlt: string; price: number }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for the date range
  const [selectedRange, setSelectedRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
  });

  useEffect(() => {
    const fetchEan = async () => {
      if (serviceId && type && token) {
        try {
          const fetchedEan = await getEanByInstallationId(serviceId, type, token);
          setEan(fetchedEan);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to fetch EAN";
          setError(errorMessage);
        }
      }
    };
    fetchEan();
  }, [serviceId, type, token]);

  const fetchData = useCallback(async () => {
    if (!ean || !token || !selectedRange.startDate || !selectedRange.endDate) return;

    setLoading(true);
    setError(null);

    const diffDays = (selectedRange.endDate.getTime() - selectedRange.startDate.getTime()) / (1000 * 60 * 60 * 24);

    let apiUrl = "";
    let method: "GET" | "POST" = "GET";
    let body: string | undefined = undefined;
    let from: string;
    let to: string;
    let isQuarterHourly = false;

    if (diffDays <= 7) {
      // 1. Quarter-hourly API (GET) - returns data with pre-calculated stats
      isQuarterHourly = true;
      const from = format(selectedRange.startDate, "yyyy-MM-dd");
      const to = format(selectedRange.endDate, "yyyy-MM-dd");

      apiUrl = `${import.meta.env.VITE_API_SERVICES}/api/v1/services/metervalues/qh/${from}/${to}/${ean}/${type}`;
      method = "GET";
    } else {
      // 2. Hourly API (POST)
      // Send Brussels-local day boundaries as naive strings — matches the
      // backend `mvdt_agg_*.dtlt` column convention (naive local time) and
      // the inclusive [start-of-day, end-of-day] range the old portal uses.
      from = format(startOfDay(selectedRange.startDate), "yyyy-MM-dd HH:mm:ss.SSS");
      to = format(endOfDay(selectedRange.endDate), "yyyy-MM-dd HH:mm:ss.SSS");
      apiUrl =
        type === "injection"
          ? `${import.meta.env.VITE_API_SERVICES}/api/v1/services/metervalues/injectionHourly/${companyId}`
          : `${import.meta.env.VITE_API_SERVICES}/api/v1/services/metervalues/offtakeHourly/${companyId}`;
      method = "POST";
      body = JSON.stringify({
        eans: [ean],
        from,
        to,
        grouped: true,
        hourly: diffDays <= 14,
        monthly: diffDays > 14,
      });
    }

    try {
      const response = await fetch(apiUrl, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        ...(body ? { body } : {}),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const responseData = await response.json();

      // Normalize response to array and store pre-calculated values if available
      let normalizedData: MeterValueItem[] = [];
      let preCalculatedStats = null;

      if (isQuarterHourly && responseData.metervalues) {
        // New API response with pre-calculated values
        normalizedData = responseData.metervalues;
        preCalculatedStats = {
          volumes: parseFloat(responseData.volumes),
          amount: parseFloat(responseData.ammount), // Note the typo in API
          avgPrice: parseFloat(responseData.avg_price),
        };
        setPreCalculatedStats(preCalculatedStats);
        // Quarter-hourly data has belpex embedded in each item, so clear separate array
        setBelpexArray([]);
      } else if (Array.isArray(responseData)) {
        normalizedData = responseData;
        setPreCalculatedStats(null);
        setBelpexArray([]);
      } else if (responseData?.mv && Array.isArray(responseData.mv)) {
        normalizedData = responseData.mv;
        // Handle separate belpex array if it exists (for hourly/monthly aggregated data)
        if (responseData.belpex && Array.isArray(responseData.belpex)) {
          setBelpexArray(
            responseData.belpex
              .filter((item: { dtlt: string; price: number | string }) => {
                const price = typeof item.price === "number" ? item.price : parseFloat(String(item.price)) || 0;
                return price > 0;
              })
              .map((item: { dtlt: string; price: number | string }) => ({
                dtlt: item.dtlt,
                price: typeof item.price === "number" ? item.price : parseFloat(String(item.price)) || 0,
              })),
          );
        } else {
          setBelpexArray([]);
        }
        setPreCalculatedStats(null);
      } else {
        normalizedData = [];
        setBelpexArray([]);
        setPreCalculatedStats(null);
      }

      setData(normalizedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch data";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [ean, token, type, selectedRange, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownload = async (kind: "csv" | "pdf") => {
    if (!ean || !serviceId || !type || !token) return;
    setExportLoading(true);
    try {
      const from = format(selectedRange.startDate, "yyyy-MM-dd");
      const to = format(selectedRange.endDate, "yyyy-MM-dd");
      const url = `${import.meta.env.VITE_API_SERVICES}/api/v1/services/metervalues/qh/${from}/${to}/${ean}/${type}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Failed to fetch data: ${response.statusText}`);
      const qh = await response.json();
      const qhData: MeterValueItem[] = Array.isArray(qh?.metervalues) ? qh.metervalues : [];
      const opts = {
        data: qhData,
        stats: statsData,
        belpexArray: [],
        ean,
        serviceId,
        type,
        dateRange: selectedRange,
      };
      if (kind === "csv") exportToCSV(opts);
      else exportToPDF(opts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setExportLoading(false);
    }
  };

  const transformDataForCharts = (
    rawData: MeterValueItem[],
    belpexSeparateArray?: Array<{ dtlt: string; price: number }>,
  ) => {
    // Transform each timestep - use max_mv from API if available, otherwise calculate
    const mvData = rawData.map((item) => {
      // Check if max_mv exists in the API response (for hourly/monthly data)
      let maxMv = 0;
      if (item.max_mv !== undefined && item.max_mv !== null && item.max_mv !== "") {
        // Use max_mv directly from API (for hourly/monthly aggregated data)
        maxMv = typeof item.max_mv === "number" ? item.max_mv : parseFloat(String(item.max_mv)) || 0;
      } else {
        // Calculate for quarter-hourly data (convert 15-min kWh to kW by multiplying by 4)
        maxMv = parseFloat(item.mv || "0") * 4;
      }

      return {
        mv: parseFloat(item.mv) || 0,
        pl: parseFloat(item.pl) || 0,
        max_mv: maxMv,
        dtlt: item.dtlt,
        date: item.date,
        hour: item.hour,
        qhour: item.qhour,
      };
    });

    // Handle belpex data - check if it's in separate array or embedded in items
    let belpexData: Array<{ price: number; dtlt: string }> = [];

    if (belpexSeparateArray && belpexSeparateArray.length > 0) {
      // Use separate belpex array (for hourly/monthly aggregated data)
      belpexData = belpexSeparateArray.map((item) => ({
        price: item.price,
        dtlt: item.dtlt,
      }));
    } else {
      // Extract from embedded belpex field (for quarter-hourly data)
      belpexData = rawData
        .filter((item) => parseFloat(item.belpex || "0") > 0)
        .map((item) => ({
          price: parseFloat(item.belpex || "0"),
          dtlt: item.dtlt,
        }));
    }

    return { mvData, belpexData };
  };

  // Transform data if available
  const chartData = data ? transformDataForCharts(data, belpexArray) : { mvData: [], belpexData: [] };
  const hasMvData = chartData.mvData.length > 0;
  const hasBelpexData = chartData.belpexData.length > 0;

  console.log(chartData);
  const isQuarterHourlyData = data && data.length > 0 && "qhour" in data[0];

  // Calculate total amount (sum of pl values)
  const totalAmount = data ? data.reduce((sum, item) => sum + parseFloat(item.pl || "0"), 0) : 0;

  // Calculate total energy delivered (sum of mv values)
  const totalEnergyDelivered = data ? data.reduce((sum, item) => sum + parseFloat(item.mv || "0"), 0) : 0;

  // Calculate average price correctly
  // The average price should be: total amount / total volume (in MWh)
  const averagePrice = totalEnergyDelivered > 0 ? (totalAmount * 1000) / totalEnergyDelivered : 0;

  // Calculate max power based on data type
  let maxPower = 0;
  if (data && data.length > 0) {
    if (isQuarterHourlyData) {
      // Quarter-hourly: convert 15-min kWh to kW by multiplying by 4
      maxPower = Math.max(...data.map((item) => parseFloat(item.mv || "0") * 4));
    } else {
      // Hourly/monthly data: use max_mv directly from API if available
      maxPower = Math.max(
        ...data.map((item) => {
          // Check if max_mv exists in the raw data
          const rawItem = item as MeterValueItem & { max_mv?: string | number };
          if (rawItem.max_mv !== undefined && rawItem.max_mv !== null && rawItem.max_mv !== "") {
            return typeof rawItem.max_mv === "number" ? rawItem.max_mv : parseFloat(String(rawItem.max_mv)) || 0;
          }
          // Fallback: for hourly data, mv is already in kWh (which equals kW over 1 hour)
          return parseFloat(item.mv || "0");
        }),
      );
    }
  }

  // const averagePrice = allPrices.length > 0 ? sumAllPrice / (totalEnergyDelivered / 1000) : 0;

  const [preCalculatedStats, setPreCalculatedStats] = useState<{
    volumes: number;
    amount: number;
    avgPrice: number;
  } | null>(null);

  // Update your stats calculation to use pre-calculated values when available
  const statsData = preCalculatedStats
    ? [
        {
          title: t("totalDelivered"),
          value: preCalculatedStats.volumes.toFixed(2),
          unit: "kWh",
        },
        {
          title: t("totalAmount"),
          value: preCalculatedStats.amount.toFixed(2),
          unit: "EUR",
        },
        {
          title: t("avgPrice"),
          value: preCalculatedStats.avgPrice.toFixed(2),
          unit: "EUR/MWh",
        },
        {
          title: t("maxPower"),
          value: (data
            ? Math.max(
                ...data.map((item) => {
                  const rawItem = item as MeterValueItem & { max_mv?: string | number };
                  if (rawItem.max_mv !== undefined && rawItem.max_mv !== null && rawItem.max_mv !== "") {
                    return typeof rawItem.max_mv === "number"
                      ? rawItem.max_mv
                      : parseFloat(String(rawItem.max_mv)) || 0;
                  }
                  // For quarter-hourly: convert 15-min kWh to kW
                  return parseFloat(item.mv || "0") * 4;
                }),
              )
            : 0
          ).toFixed(2),
          unit: "kW",
        },
      ]
    : [
        {
          title: t("totalDelivered"),
          value: totalEnergyDelivered.toFixed(2),
          unit: "kWh",
        },
        {
          title: t("totalAmount"),
          value: totalAmount.toFixed(2),
          unit: "EUR",
        },
        {
          title: t("avgPrice"),
          value: averagePrice.toFixed(2),
          unit: "EUR/MWh",
        },
        {
          title: t("maxPower"),
          value: maxPower.toFixed(2),
          unit: "kW",
        },
      ];

  return (
    <div className="my-20 md:mx-14 text-primary-600">
      <div className="flex justify-between">
        <h1 className="text-3xl font-semibold">{t("serviceDashboard")}</h1>
        {!isMobile && (
          <button
            onClick={handleBack}
            className="mb-4 bg-primary-600 text-white py-2 px-4 rounded hover:bg-primary-700"
          >
            {t("back")}
          </button>
        )}
      </div>

      <div className="bg-white border mt-8 flex flex-col gap-12">
        <div className="flex flex-col md:flex-row md:items-center gap-4 border-b px-10 py-5">
          <div>
            <div className="border py-2 pr-10 px-3 rounded text-sm">
              {ean} - {serviceId}
            </div>
          </div>

          <div>
            <CustomDateRangePicker
              onChange={(range) => {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                  if (range.startDate && range.endDate) {
                    setSelectedRange({
                      startDate: range.startDate,
                      endDate: range.endDate,
                    });
                  }
                }, 600);
              }}
            />
          </div>

          <div className="flex gap-2 md:ml-auto">
            <button
              onClick={() => handleDownload("csv")}
              disabled={!data || loading || exportLoading}
              className="flex items-center gap-2 border border-primary-300 text-primary-600 py-2 px-4 rounded text-sm hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <DownloadIcon width={16} height={16} />
              {t("downloadCSV")}
            </button>
            <button
              onClick={() => handleDownload("pdf")}
              disabled={!data || loading || exportLoading}
              className="flex items-center gap-2 bg-primary-600 text-white py-2 px-4 rounded text-sm hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <DownloadIcon width={16} height={16} stroke="white" />
              {t("downloadPDF")}
            </button>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 md:flex md:gap-32 px-12 gap-y-8 gap-x-4">
            {statsData.map((stat, index) => (
              <div key={index}>
                <div className="flex mb-3 gap-1">
                  <p className="text-sm text-primary-400">{stat.title}</p>
                  <div className="tooltip" data-tip="More info about this chart">
                    <InfoIcon />
                  </div>
                </div>
                <p className="md:text-4xl text-lg font-semibold text-primary-600">
                  {stat.value} <span className="text-sm">{stat.unit}</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-red-500 mt-4">
            {t("error")} {error}
          </p>
        )}
        {loading && <Loading />}

        {!loading && !error && (
          <div className="mt-4 md:px-12 pl-2">
            <div className="mt-6">
              <p className="text-primary-600  text-base md:text-sm font-semibold"> {t("dayAheadMarkedPrice")}</p>
              {hasBelpexData && <DayAheadMarketPriceChart belpexData={chartData.belpexData} />}
              {hasMvData && (
                <div>
                  <p className="text-primary-600 text-base md:text-sm font-semibold  mt-24">
                    {type === 'injection' ? 'Injection (kWh)' : t("supplyPrice")}
                  </p>
                  <SupplyChart mvData={chartData.mvData} type={type} />

                  <p className="text-primary-600 text-base md:text-sm font-semibold  mt-24"> {t("totalAmountChart")}</p>
                  <TotalAmountChart mvData={chartData.mvData} />

                  <p className="text-primary-600  text-base md:text-sm font-semibold  mt-24">{t("peakPower")}</p>
                  <PeakPowerChart mvData={chartData.mvData} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ServiceDashboardPage;
